import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { SettingsSidebar } from '@/components/settings/SettingsSidebar';
import { PreferencesTabContent } from '@/components/settings/PreferencesTabContent';
import { TagsTabContent } from '@/components/settings/TagsTabContent';
import { StatusesTabContent } from '@/components/settings/StatusesTabContent';
import { PrioritiesTabContent } from '@/components/settings/PrioritiesTabContent';
import { EventsTabContent } from '@/components/settings/EventsTabContent';
import { WorkspaceSettingsPage } from './WorkspaceSettingsPage';
import { MembersSettingsPage } from './MembersSettingsPage';
import { ProfileSettingsPage } from './ProfileSettingsPage';
import { TeamSettingsPage } from './TeamSettingsPage';
import { IntegrationsPage } from '@/pages/workspace/IntegrationsPage';
import { IntegrationDetailPage } from '@/pages/workspace/IntegrationDetailPage';
import { ProjectList } from '@/components/projects/ProjectList';
import { cn } from '@/lib/utils';
import type { SettingsTabId } from '@/components/settings/SettingsSidebar';

const VALID_TAB_IDS: SettingsTabId[] = [
  'profile',
  'workspace',
  'projects',
  'members',
  'integrations',
  'tags',
  'statuses',
  'priorities',
  'insights',
  'billing',
  'usage',
  'events',
  'models',
  'pull-requests',
  'instructions',
  'sandbox',
  'api-keys',
  'preferences',
  'team'
];

function PlaceholderContent({ label }: { label: string }) {
  return (
    <div className="w-3xl mx-auto min-h-full flex-1 px-8 py-10 xl:border-x">
      <h1 className="text-xl font-semibold tracking-tight">{label}</h1>
    </div>
  );
}

export function SettingsPage() {
  const { id: workspaceId, tabId } = useParams<{ id: string; tabId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId);

  const segments = location.pathname.split('/');
  const settingsIndex = segments.indexOf('settings');
  const activeTabId = (
    settingsIndex >= 0 && segments[settingsIndex + 1] ? segments[settingsIndex + 1] : 'workspace'
  ) as SettingsTabId;
  const integrationId =
    activeTabId === 'integrations' ? (segments[settingsIndex + 2] ?? undefined) : undefined;
  const teamId = activeTabId === 'team' ? (segments[settingsIndex + 2] ?? undefined) : undefined;

  useEffect(() => {
    if (workspaceId && !tabId && !integrationId && !teamId) {
      navigate(`/workspace/${workspaceId}/settings/workspace`, { replace: true });
    }
  }, [workspaceId, tabId, integrationId, teamId, navigate]);

  if (!workspace) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center">
        Workspace not found.
      </div>
    );
  }

  if (!tabId && !integrationId && !teamId) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center">
        Redirecting…
      </div>
    );
  }

  let content: React.ReactNode = null;

  if (!VALID_TAB_IDS.includes(activeTabId)) {
    content = <PlaceholderContent label="Settings" />;
  } else {
    switch (activeTabId) {
      case 'workspace':
        content = <WorkspaceSettingsPage workspaceId={workspaceId!} />;
        break;
      case 'projects':
        content = (
          <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
            <ProjectList workspaceId={workspaceId!} />
          </div>
        );
        break;
      case 'members':
        content = <MembersSettingsPage workspaceId={workspaceId!} />;
        break;
      case 'integrations':
        content = integrationId ? <IntegrationDetailPage /> : <IntegrationsPage />;
        break;
      case 'tags':
        content = <TagsTabContent workspaceId={workspaceId!} />;
        break;
      case 'statuses':
        content = <StatusesTabContent workspaceId={workspaceId!} />;
        break;
      case 'priorities':
        content = <PrioritiesTabContent workspaceId={workspaceId!} />;
        break;
      case 'profile':
        content = <ProfileSettingsPage />;
        break;
      case 'preferences':
        content = <PreferencesTabContent />;
        break;
      case 'events':
        content = <EventsTabContent />;
        break;
      case 'team':
        content = teamId ? (
          <TeamSettingsPage workspaceId={workspaceId!} teamId={teamId} />
        ) : (
          <PlaceholderContent label="Team Settings" />
        );
        break;
      default:
        content = (
          <PlaceholderContent
            label={activeTabId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
          />
        );
    }
  }

  return (
    <div className="bg-background flex flex-1 overflow-hidden">
      <aside className="bg-sidebar shrink-0">
        <SettingsSidebar activeTabId={activeTabId} showBackToWorkspace activeTeamId={teamId} />
      </aside>
      <main className="bg-sidebar relative flex flex-1">
        <div className={cn('flex-1 overflow-y-auto border-l')}>{content}</div>
      </main>
    </div>
  );
}
